import { Entypo } from "@expo/vector-icons";
import { useMutation } from "@tanstack/react-query";
import { Text } from "@ui-kitten/components";
import classNames from "classnames";
import Button from "components/Button";
import Input from "components/Input";
import Spacer from "components/Spacer";
import {
  initialPrimaryUser,
  setIsEditingPrimaryUser,
  setPrimaryUser,
  setSecondaryUsers,
  setSelectedBillingInterval,
  setSelectedPlanPackage,
} from "features/slices/buyFlowSlice";
import {
  setQuantity,
  setSelectedCmsPlan,
  setSignupCharges,
  setStep,
} from "features/slices/shopPagePhaseTwoSlice";
import { BillingInterval } from "features/types";
import { useAppDispatch } from "hooks/useAppDispatch";
import { useAppSelector } from "hooks/useAppSelector";
import useAuth from "hooks/useAuth";
import useCurrentUser from "hooks/useCurrentUser";
import { getSanityImage } from "lib/sanity";
import { CmsPlan, ShopPagePhaseTwo } from "lib/sanity.types";
import isEmpty from "lodash/isEmpty";
import { fetchSignupChargeRequest } from "queries/account.queries";
import { trackConversion } from "queries/conversion.queries";
import { useFetchPlanPackagesQuery } from "queries/planPackage.queries";
import { useFetchTenantBrandingQuery } from "queries/tenant.queries";
import React, { useEffect, useMemo, useRef } from "react";
import { Pressable, View } from "react-native";
import { toGiftPage } from "utils/navigationUtils";
import { getPlanType } from "utils/planPackageUtils";
import OrderSummary from "./components/OrderSummary";
import PlanContainer from "./components/PlanContainer";
import { planIdProperty } from "./helpers";

const StepTwo = ({
  shopPage,
  scrollToNext,
}: {
  shopPage: ShopPagePhaseTwo;
  scrollToNext: () => void;
}) => {
  const { group, selectedCmsPlan, quantity } = useAppSelector(
    (state) => state.shopPagePhaseTwo
  );
  const { selectedBillingInterval, selectedPlanPackage } = useAppSelector(
    (state) => state.buyFlow
  );
  const dispatch = useAppDispatch();

  const auth = useAuth();

  const { currentUser } = useCurrentUser();
  const { data: branding } = useFetchTenantBrandingQuery();
  const { data: planPackages } = useFetchPlanPackagesQuery();
  const { mutateAsync: fetchSignupCharge } = useMutation(
    fetchSignupChargeRequest
  );
  const { mutateAsync: track } = useMutation(trackConversion);

  const viewContainer = useRef<HTMLDivElement | null>(null);
  const Cmsplans = shopPage.plans.filter((plan) => plan.group === group);
  const CmsplanIds = Cmsplans.map((plan) => plan[planIdProperty]);

  const billingIntervals = [
    { label: "Payment Plan", value: "MONTHLY" },
    { label: "Pay In Full", value: "FULL_SEASON" },
  ];

  const actualPlanPackages = planPackages?.filter((planPackage) =>
    CmsplanIds.includes(planPackage.id)
  );

  const selectedGroup = useMemo(
    () => shopPage.groups.find((shopGroup) => shopGroup.name === group),
    [group, shopPage.groups]
  );

  const getSeatMapUrl = useMemo(() => {
    if (!selectedCmsPlan) return undefined;
    if (selectedGroup?.seasonTicketPlan) {
      return undefined;
    }
    const seatMapName = selectedCmsPlan.seatMapName;
    const assetRef = shopPage.seatMaps.find(
      (seatMap) => seatMap.name === seatMapName
    )?.image.asset._ref;

    if (!assetRef) return undefined;

    return getSanityImage(assetRef);
  }, [selectedCmsPlan, selectedGroup]);

  const fetchSignupCharges = async () => {
    const result = [];
    for (const planPackage of actualPlanPackages!) {
      for (const billingInterval of planPackage.billingIntervals) {
        const signupCharge = await fetchSignupCharge({
          planPackageIds: [planPackage.id],
          billingInterval,
        });
        result.push({
          planPackageId: planPackage.id,
          billingInterval,
          signupCharge,
        });
      }
    }
    dispatch(setSignupCharges(result));
  };

  const isVoucherPlan =
    !isEmpty(actualPlanPackages) &&
    getPlanType(actualPlanPackages![0]) === "VOUCHER";

  const shouldShowBillingInterval = useMemo(() => {
    if (isEmpty(actualPlanPackages)) return false;
    return actualPlanPackages![0].billingIntervals.length > 1;
  }, [planPackages]);

  const handleContinueBtnPress = async () => {
    dispatch(setStep(3));
    scrollToNext();
    await track({
      step: "PROCEED_TO_USER_INFO",
      flow: "SHOP_PHASE_TWO",
    });
  };

  const setActualPlanPackageInBuyFlowSlice = (plan: CmsPlan) => {
    const actualPlanPackage = actualPlanPackages?.find(
      (p) => p.id === plan[planIdProperty]
    );
    if (actualPlanPackage !== undefined) {
      dispatch(setSelectedPlanPackage(actualPlanPackage));
    }
  };

  useEffect(() => {
    if (group === "") return;
    dispatch(setSelectedCmsPlan(Cmsplans[0]));
  }, [group]);

  useEffect(() => {
    if (isEmpty(actualPlanPackages)) return;
    dispatch(
      setSelectedBillingInterval(actualPlanPackages![0].billingIntervals[0])
    );

    fetchSignupCharges();
    setActualPlanPackageInBuyFlowSlice(Cmsplans[0]);
  }, [planPackages]);

  useEffect(() => {
    if (!auth.isLoggedIn || !currentUser) return;
    dispatch(
      setPrimaryUser({
        ...initialPrimaryUser,
        id: currentUser.id,
        firstName: currentUser.firstName,
        lastName: currentUser.lastName,
        email: currentUser.email,
        phone: currentUser.phone,
        imageIdentifier: currentUser.imageIdentifier || "",
        userFields: currentUser.userFields || {},
      })
    );
    dispatch(setSecondaryUsers([]));
    dispatch(setIsEditingPrimaryUser(false));
  }, [auth.isLoggedIn, currentUser, dispatch]);

  return (
    <div ref={(ref) => (viewContainer.current = ref)}>
      <Text category="h6">Select Your Plan</Text>
      <View className="flex-col justify-center">
        {shouldShowBillingInterval && (
          <View
            className="mt-4 flex-row justify-center items-center border rounded-lg overflow-hidden text-center text-white"
            style={{ backgroundColor: branding?.primaryColor }}
          >
            {billingIntervals.map((interval) => {
              const selected = interval.value === selectedBillingInterval;
              return (
                <Pressable
                  key={interval.value}
                  className={classNames(
                    "p-1 border rounded-md border-transparent grow text-white"
                  )}
                  onPress={() =>
                    dispatch(
                      setSelectedBillingInterval(
                        interval.value as BillingInterval
                      )
                    )
                  }
                >
                  <div
                    className={classNames("rounded-md p-2", {
                      "text-black": selected,
                      "bg-white": selected,
                    })}
                  >
                    {interval.label}
                  </div>
                </Pressable>
              );
            })}
          </View>
        )}
        <View className="mt-4 grid gap-4 relative">
          <View>
            {Cmsplans.map((plan) => {
              const isPlanSelected = selectedCmsPlan?._key === plan._key;

              return (
                <View key={plan._key} className="mb-4">
                  <View
                    className={classNames("p-2 rounded-xl mb-2", {
                      "border-[4px]": isPlanSelected,
                    })}
                    style={{
                      borderColor: branding?.primaryColor,
                    }}
                  >
                    <PlanContainer
                      plan={plan}
                      onPlanPress={setActualPlanPackageInBuyFlowSlice}
                      isPlanSelected={isPlanSelected}
                      hidePrice={false}
                    ></PlanContainer>
                  </View>
                  {isVoucherPlan && selectedCmsPlan?._key === plan._key && (
                    <View className="mb-2 flex-row justify-center items-center gap-4">
                      <Pressable
                        onPress={() =>
                          dispatch(setQuantity(Math.max(quantity - 1, 1)))
                        }
                      >
                        <Entypo name="minus" size={24} color="black" />
                      </Pressable>
                      <Input style={{ width: 50 }} value={String(quantity)} />
                      <Pressable
                        testID="quantity-plus-button"
                        onPress={() => {
                          const max =
                            selectedPlanPackage?.numOfPlansAllowedPerMember;
                          if (!max || max < 0 || quantity + 1 <= max) {
                            dispatch(setQuantity(quantity + 1));
                          }
                        }}
                      >
                        <Entypo name="plus" size={24} color="black" />
                      </Pressable>
                    </View>
                  )}
                  {isPlanSelected && (
                    <View className="md:hidden">
                      <Spacer>
                        {!!getSeatMapUrl && <img src={getSeatMapUrl} />}
                        <Button
                          onPress={handleContinueBtnPress}
                          style={{
                            marginTop: 24,
                            marginBottom: 36,
                            width: 300,
                            alignSelf: "center",
                          }}
                        >
                          CONTINUE
                        </Button>
                        {selectedCmsPlan.giftingEnabled && (
                          <a
                            className="text-center cursor-pointer text-xl font-semibold"
                            onClick={() =>
                              toGiftPage(selectedCmsPlan[planIdProperty])
                            }
                          >
                            Sending as a gift?
                          </a>
                        )}
                      </Spacer>
                      <OrderSummary />
                    </View>
                  )}
                </View>
              );
            })}
          </View>
        </View>
      </View>
    </div>
  );
};

export default StepTwo;
